import { site, heroImage } from '@/data/site';
import { useScrollY } from '@/hooks/useScrollY';
import { useInView } from '@/hooks/useInView';
import { useCountUp } from '@/hooks/useCountUp';
import Stars from '@/components/base/Stars';
import GoogleWordmark from '@/components/base/GoogleWordmark';

const HEADLINE_WORDS = ['Ihr', 'Garten', 'in', 'besten', 'Händen.'];

export default function Hero() {
  const y = useScrollY();
  const shift = Math.min(y * 0.18, 140);
  const { ref, inView } = useInView<HTMLDivElement>(0.2);
  const rating = useCountUp(5, inView, 1700);
  const ratingLabel = rating.toFixed(1).replace('.', ',');

  return (
    <section
      id="top"
      className="relative w-full min-h-[100svh] flex items-center justify-center overflow-hidden bg-foreground-950 pt-28 pb-24"
    >
      {/* Background: neutral dark, slowly zooming, subtle parallax */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 animate-hero-zoom">
          <img
            src={heroImage}
            alt="Gepflegter privater Garten mit grünem Rasen, Beeten und geschnittener Hecke"
            title="Gartenpflege vom Fachbetrieb Arnold Gartenpflege"
            className="w-full h-[130%] object-cover object-[50%_42%]"
            style={{ transform: `translate3d(0, ${shift}px, 0) scale(1.08)` }}
          />
        </div>
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/70" />
      </div>

      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-8 text-center flex flex-col items-center">
        {/* Badge */}
        <div
          className="hero-fade inline-flex items-center gap-2 rounded-full border border-accent-400/40 bg-background-50/[0.07] backdrop-blur-sm px-4 py-2"
          style={{ animationDelay: '0.05s' }}
        >
          <i className="ri-map-pin-2-fill text-accent-400" />
          <span className="font-label text-xs md:text-sm tracking-wide text-background-100">
            Fachbetrieb aus Röttenbach bei Erlangen
          </span>
        </div>

        {/* Headline – Wort für Wort */}
        <h1 className="mt-7 font-heading font-semibold leading-[1.04] text-background-50 text-4xl sm:text-6xl md:text-7xl lg:text-[5.25rem]">
          {HEADLINE_WORDS.map((word, index) => (
            <span
              key={`${word}-${index}`}
              className={`hero-word mr-[0.22em] last:mr-0 ${index >= 3 ? 'text-accent-400' : ''}`}
              style={{ animationDelay: `${0.18 + index * 0.09}s` }}
            >
              {word}
            </span>
          ))}
        </h1>

        {/* Subline */}
        <p
          className="hero-fade mt-7 max-w-2xl font-body text-base md:text-xl leading-relaxed text-background-100/85"
          style={{ animationDelay: '0.72s' }}
        >
          Gartenpflege, Baumpflege &amp; Winterdienst in{" "}
          <strong className="font-semibold text-background-50">{site.region}</strong> – zuverlässig,
          sauber und zum fairen Preis.
        </p>

        {/* Rating */}
        <div
          ref={ref}
          className="hero-fade mt-8 inline-flex flex-wrap items-center justify-center gap-x-4 gap-y-2 rounded-xl border border-background-50/15 bg-background-50/[0.07] backdrop-blur-sm px-5 py-3.5"
          style={{ animationDelay: '0.84s' }}
        >
          <span className="flex items-center gap-2.5">
            <span className="font-heading text-3xl md:text-4xl font-semibold text-background-50 leading-none tabular-nums">
              {ratingLabel}
            </span>
            <Stars size="text-xl" animated start={inView} />
          </span>
          <span className="hidden sm:inline w-px h-7 bg-background-50/20" />
          <span className="flex items-center gap-1.5 font-label text-sm text-background-100/90">
            auf <GoogleWordmark className="text-base" /> Bewertungen
          </span>
        </div>

        {/* CTAs */}
        <div
          className="hero-fade mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto"
          style={{ animationDelay: '0.96s' }}
        >
          <a
            href="#kontakt"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-accent-500 hover:bg-accent-600 text-foreground-950 font-label font-bold text-base px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-mail-send-line text-lg" />
            Kostenlos anfragen
          </a>
          <a
            href={site.phoneHref}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-background-50/40 bg-background-50/[0.06] hover:bg-background-50/15 backdrop-blur-sm text-background-50 font-label font-bold text-base px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-fill text-lg" />
            Jetzt anrufen
          </a>
        </div>

        {/* Region chips */}
        <div
          className="hero-fade mt-10 flex flex-wrap items-center justify-center gap-2"
          style={{ animationDelay: '1.08s' }}
        >
          <span className="font-label text-[11px] uppercase tracking-[0.24em] text-background-100/60 mr-1">
            Im Einsatz in
          </span>
          {site.cities.map((city) => (
            <span
              key={city}
              className="rounded-full border border-background-50/15 bg-background-50/[0.06] px-3.5 py-1.5 font-label text-xs text-background-100 whitespace-nowrap"
            >
              {city}
            </span>
          ))}
        </div>
      </div>

      {/* Scroll hint */}
      <a
        href="#leistungen"
        aria-label="Zu den Leistungen scrollen"
        className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex-col items-center gap-2 text-background-100/60 hover:text-background-50 transition-colors cursor-pointer"
      >
        <span className="font-label text-[11px] uppercase tracking-[0.3em]">Leistungen</span>
        <i className="ri-arrow-down-line text-xl animate-soft-float" />
      </a>
    </section>
  );
}